import React, { useMemo } from "react";

import type { FxIntentNode } from "./NarrativeSonics.types";

export interface FxRegionOverlayProps {
  viewportStartSec: number;
  viewportEndSec: number;
  pxPerSec: number;
  nodes: FxIntentNode[];
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function nodeStyles(node: FxIntentNode) {
  const strength = clamp(node.strength, 0, 1);
  switch (node.type) {
    case "swell":
      return {
        fill: `linear-gradient(180deg, rgba(14,165,233,${0.12 + strength * 0.24}), rgba(34,211,238,${0.08 + strength * 0.18}))`,
        edge: `rgba(125,211,252,${0.26 + strength * 0.24})`,
        glow: `rgba(14,165,233,${0.12 + strength * 0.18})`,
        label: "rgba(224,242,254,0.92)",
        text: "SWELL",
      };
    case "duck":
      return {
        fill: `linear-gradient(180deg, rgba(245,158,11,${0.12 + strength * 0.24}), rgba(251,191,36,${0.08 + strength * 0.16}))`,
        edge: `rgba(253,224,71,${0.24 + strength * 0.24})`,
        glow: `rgba(245,158,11,${0.12 + strength * 0.18})`,
        label: "rgba(254,243,199,0.94)",
        text: "DUCK",
      };
    case "release":
      return {
        fill: `linear-gradient(180deg, rgba(139,92,246,${0.12 + strength * 0.22}), rgba(192,132,252,${0.08 + strength * 0.16}))`,
        edge: `rgba(216,180,254,${0.24 + strength * 0.22})`,
        glow: `rgba(139,92,246,${0.1 + strength * 0.16})`,
        label: "rgba(243,232,255,0.94)",
        text: "REL",
      };
    default:
      return {
        fill: "rgba(255,255,255,0.08)",
        edge: "rgba(255,255,255,0.14)",
        glow: "rgba(255,255,255,0.08)",
        label: "rgba(255,255,255,0.88)",
        text: "FX",
      };
  }
}

export default function FxRegionOverlay({
  viewportStartSec,
  viewportEndSec,
  pxPerSec,
  nodes,
}: FxRegionOverlayProps): JSX.Element | null {
  const visibleNodes = useMemo(
    () => nodes.filter((node) => node.endSec >= viewportStartSec && node.startSec <= viewportEndSec),
    [nodes, viewportEndSec, viewportStartSec],
  );

  if (visibleNodes.length === 0) return null;

  return (
    <div className="pointer-events-none absolute inset-0 z-[29]" data-oc-fx-region-overlay="1" aria-hidden>
      <div className="absolute left-0 right-0 top-[46px] h-8 rounded-2xl border border-white/6 bg-white/[0.012]" />
      {visibleNodes.map((node) => {
        const leftPx = Math.max(0, node.startSec * pxPerSec);
        const widthPx = Math.max(8, (node.endSec - node.startSec) * pxPerSec);
        const styles = nodeStyles(node);
        return (
          <div
            key={node.id}
            className="absolute rounded-[14px] border"
            style={{
              left: leftPx,
              top: 49,
              width: widthPx,
              height: 12,
              borderColor: styles.edge,
              background: styles.fill,
              opacity: 0.68 + clamp(node.strength, 0, 1) * 0.24,
              boxShadow: `0 0 0 1px ${styles.edge}, 0 8px 18px ${styles.glow}`,
            }}
          >
            {widthPx >= 56 ? (
              <div className="truncate px-2 pt-[1px] text-[8px] font-black uppercase tracking-[0.16em]" style={{ color: styles.label }}>
                {styles.text}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}